// App root
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { toast } from "sonner";
import { log } from "@/lib/logger";
import { RouteErrorBoundary } from "@/components/errors/RouteErrorBoundary";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminProtectedRoute } from "@/components/AdminProtectedRoute";
import { OwnerOnlyRoute } from "@/components/OwnerOnlyRoute";
import { ModuleProtectedRoute } from "@/components/ModuleProtectedRoute";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { IdleLogoutMount } from "@/components/IdleLogoutMount";
import { ThemeScheduleProvider } from "@/components/ThemeScheduleProvider";
import { renderModuleRoutes } from "@/routes/ModuleRoutes";


import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Lazy-loaded routes (code-split per page)
const PomodoroHistory = lazy(() => import("./pages/PomodoroHistory"));

const SetupMfa = lazy(() => import("./pages/SetupMfa"));
const AccountSecurity = lazy(() => import("./pages/AccountSecurity"));
const ModuleSelection = lazy(() => import("./pages/ModuleSelection"));

const Questions = lazy(() => import("./pages/concursos/ConcursosQuestions"));
const QuestionDetail = lazy(() => import("./pages/QuestionDetail"));
const History = lazy(() => import("./pages/History"));
const Profile = lazy(() => import("./pages/Profile"));
const Simulations = lazy(() => import("./pages/Simulations"));
const SimulationPlay = lazy(() => import("./pages/SimulationPlay"));
const Ranking = lazy(() => import("./pages/Ranking"));
const Flashcards = lazy(() => import("./pages/Flashcards"));
const FlashcardReview = lazy(() => import("./pages/FlashcardReview"));
const FavoritesStudy = lazy(() => import("./pages/FavoritesStudy"));
const StudyStats = lazy(() => import("./pages/StudyStats"));
const PomodoroStudy = lazy(() => import("./pages/PomodoroStudy"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Challenges = lazy(() => import("./pages/Challenges"));

const StudyGroups = lazy(() => import("./pages/StudyGroups"));
const StudyGroupDetail = lazy(() => import("./pages/StudyGroupDetail"));
const SmartReview = lazy(() => import("./pages/SmartReview"));
const DisciplineGoals = lazy(() => import("./pages/DisciplineGoals"));
const Achievements = lazy(() => import("./pages/Achievements"));
const EnemAchievements = lazy(() => import("./pages/enem/EnemAchievements"));
const Favorites = lazy(() => import("./pages/Favorites"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminQuestions = lazy(() => import("./pages/admin/AdminQuestions"));
const AdminDisciplines = lazy(() => import("./pages/admin/AdminDisciplines"));
const AdminExamBoards = lazy(() => import("./pages/admin/AdminExamBoards"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminClasses = lazy(() => import("./pages/admin/AdminClasses"));
const AdminFlashcards = lazy(() => import("./pages/admin/AdminFlashcards"));
const AdminSimulations = lazy(() => import("./pages/admin/AdminSimulations"));
const AdminCreateSimulation = lazy(() => import("./pages/admin/AdminCreateSimulation"));
const AdminSubjectLinking = lazy(() => import("./pages/admin/AdminSubjectLinking"));
const AdminPoliciaisWhitelist = lazy(() => import("./pages/admin/AdminPoliciaisWhitelist"));
const AdminAiAutoCreated = lazy(() => import("./pages/admin/AdminAiAutoCreated"));
const AdminSubjectSuggestions = lazy(() => import("./pages/admin/AdminSubjectSuggestions"));
const AdminDisciplineConsolidation = lazy(() => import("./pages/admin/AdminDisciplineConsolidation"));
const AdminFlashcardGenerator = lazy(() => import("./pages/admin/AdminFlashcardGenerator"));
const AdminEnemQuestions = lazy(() => import("./pages/admin/AdminEnemQuestions"));
const AdminEnemImport = lazy(() => import("./pages/admin/AdminEnemImport"));
const AdminEnemDataImport = lazy(() => import("./pages/admin/AdminEnemDataImport"));
const AdminEnemExtractionReview = lazy(() => import("./pages/admin/AdminEnemExtractionReview"));
const AdminFonteImport = lazy(() => import("./pages/admin/AdminFonteImport"));
const AdminTecImport = lazy(() => import("./pages/admin/AdminTecImport"));
const AdminQuestioneiReinfer = lazy(() => import("./pages/admin/AdminQuestioneiReinfer"));
const AdminQuestionQualityAudit = lazy(() => import("./pages/admin/AdminQuestionQualityAudit"));
const AdminDisciplineStats = lazy(() => import("./pages/admin/AdminDisciplineStats"));
const AdminCourseFinancials = lazy(() => import("./pages/admin/AdminCourseFinancials"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminOabImport = lazy(() => import("./pages/admin/AdminOabImport"));
const AdminOabQuestions = lazy(() => import("./pages/admin/AdminOabQuestions"));
const AdminPlanRequests = lazy(() => import("./pages/admin/AdminPlanRequests"));
const AdminScrapersMonitor = lazy(() => import("./pages/admin/AdminScrapersMonitor"));
const AdminResidenciaScrapers = lazy(() => import("./pages/admin/AdminResidenciaScrapers"));
const RevalidaImport = lazy(() => import("./pages/admin/RevalidaImport"));
const RevalidaDiscursivas = lazy(() => import("./pages/residencia/RevalidaDiscursivas"));
const AdminResidenciaQuestions = lazy(() => import("./pages/admin/AdminResidenciaQuestions"));
const AdminRevalidaQuestions = lazy(() => import("./pages/admin/AdminRevalidaQuestions"));
const AdminRevalidaEssays = lazy(() => import("./pages/admin/AdminRevalidaEssays"));
const AdminExportImport = lazy(() => import("./pages/admin/AdminExportImport"));
const AdminTenantSync = lazy(() => import("./pages/admin/AdminTenantSync"));
const AdminSimulationDetail = lazy(() => import("./pages/admin/AdminSimulationDetail"));
const AdminAIUsage = lazy(() => import("./pages/admin/AdminAIUsage"));
const AdminSecurityMonitor = lazy(() => import("./pages/admin/AdminSecurityMonitor"));
const AdminSystemRatings = lazy(() => import("./pages/admin/AdminSystemRatings"));
const AdminQuestionReports = lazy(() => import("./pages/admin/AdminQuestionReports"));
const AdminAiPendingQueue = lazy(() => import("./pages/admin/AdminAiPendingQueue"));
const AdminPlanPricing = lazy(() => import("./pages/admin/AdminPlanPricing"));
const AdminDevPlanPricingB2C = lazy(() => import("./pages/admin/dev/AdminDevPlanPricingB2C"));
const AdminBilling = lazy(() => import("./pages/admin/AdminBilling"));
const EnemDashboard = lazy(() => import("./pages/enem/EnemDashboard"));
const EnemQuestions = lazy(() => import("./pages/enem/EnemQuestions"));
const EnemSimulations = lazy(() => import("./pages/enem/EnemSimulations"));
const EnemCreateSimulation = lazy(() => import("./pages/enem/EnemCreateSimulation"));
const EnemErrorPatterns = lazy(() => import("./pages/enem/EnemErrorPatterns"));
const EnemSimulationResult = lazy(() => import("./pages/enem/EnemSimulationResult"));
const EnemQuestionReview = lazy(() => import("./pages/enem/EnemQuestionReview"));
const EnemWeakAreasStudy = lazy(() => import("./pages/enem/EnemWeakAreasStudy"));
const EnemSmartReview = lazy(() => import("./pages/enem/EnemSmartReview"));
const EnemFullSimulation = lazy(() => import("./pages/enem/EnemFullSimulation"));
const EnemSettings = lazy(() => import("./pages/enem/EnemSettings"));
const EnemRanking = lazy(() => import("./pages/enem/EnemRanking"));
const EnemStudyPlan = lazy(() => import("./pages/enem/EnemStudyPlan"));
const ConcursosStudyPlan = lazy(() => import("./pages/concursos/ConcursosStudyPlan"));
const ConcursosSelfAssessment = lazy(() => import("./pages/concursos/ConcursosSelfAssessment"));
const EnemSelfAssessment = lazy(() => import("./pages/enem/EnemSelfAssessment"));
const EnemRedacao = lazy(() => import("./pages/enem/EnemRedacao"));
const EnemRedacaoEditor = lazy(() => import("./pages/enem/EnemRedacaoEditor"));
const EnemRedacaoResultado = lazy(() => import("./pages/enem/EnemRedacaoResultado"));
const EnemRedacaoHistorico = lazy(() => import("./pages/enem/EnemRedacaoHistorico"));
const OabStudyPlan = lazy(() => import("./pages/oab/OabStudyPlan"));
const CnuStudyPlan = lazy(() => import("./pages/cnu/CnuStudyPlan"));
const PoliciaisStudyPlan = lazy(() => import("./pages/policiais/PoliciaisStudyPlan"));
const JuridicasStudyPlan = lazy(() => import("./pages/juridicas/JuridicasStudyPlan"));
const TribunaisStudyPlan = lazy(() => import("./pages/tribunais/TribunaisStudyPlan"));
const BancarioStudyPlan = lazy(() => import("./pages/bancario/BancarioStudyPlan"));
const MilitaresStudyPlan = lazy(() => import("./pages/militares/MilitaresStudyPlan"));
const ResidenciaStudyPlan = lazy(() => import("./pages/residencia/ResidenciaStudyPlan"));
const VestibularesStudyPlan = lazy(() => import("./pages/vestibulares/VestibularesStudyPlan"));
const ColegiosStudyPlan = lazy(() => import("./pages/colegios/ColegiosStudyPlan"));
const EnemAnalytics = lazy(() => import("./pages/enem/EnemAnalytics"));
const EnemStats = lazy(() => import("./pages/enem/EnemStats"));
const EnemOndeEuEstou = lazy(() => import("./pages/enem/EnemOndeEuEstou"));
const EnemMapaUFs = lazy(() => import("./pages/enem/EnemMapaUFs"));
const EnemBuscarEscola = lazy(() => import("./pages/enem/EnemBuscarEscola"));
const ConcursosSettings = lazy(() => import("./pages/ConcursosSettings"));
const CreateSimulation = lazy(() => import("./pages/CreateSimulation"));
const SharedSimulation = lazy(() => import("./pages/SharedSimulation"));
const VideoLessons = lazy(() => import("./pages/VideoLessons"));
const VideoaulasHome = lazy(() => import("./pages/VideoaulasHome"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const LessonPlayer = lazy(() => import("./pages/LessonPlayer"));
const AdminCourses = lazy(() => import("./pages/admin/AdminCourses"));
const AdminTeachers = lazy(() => import("./pages/admin/AdminTeachers"));
const OabDashboard = lazy(() => import("./pages/oab/OabDashboard"));
const OabQuestions = lazy(() => import("./pages/oab/OabQuestions"));
const OabSimulations = lazy(() => import("./pages/oab/OabSimulations"));
const OabCreateSimulation = lazy(() => import("./pages/oab/OabCreateSimulation"));
const OabRanking = lazy(() => import("./pages/oab/OabRanking"));
const OabSettings = lazy(() => import("./pages/oab/OabSettings"));
const OabAnalytics = lazy(() => import("./pages/oab/OabAnalytics"));
const OabSelfAssessment = lazy(() => import("./pages/oab/OabSelfAssessment"));
// Páginas dos 9 módulos clone (Tribunais, Policiais, Jurídicas, CNU, Bancário,
// Militares, Residência, Colégios, Vestibulares) agora ficam em
// src/routes/ModuleRoutes.tsx via MODULE_PAGES + renderModuleRoutes().

const Plans = lazy(() => import("./pages/Plans"));
const ModulePurchase = lazy(() => import("./pages/ModulePurchase"));
const MyCourses = lazy(() => import("./pages/MyCourses"));
const VideoaulasHistory = lazy(() => import("./pages/VideoaulasHistory"));
const VideoaulasStats = lazy(() => import("./pages/VideoaulasStats"));
const PlanosB2C = lazy(() => import("./pages/PlanosB2C"));
const CheckoutB2C = lazy(() => import("./pages/CheckoutB2C"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const MinhasAssinaturas = lazy(() => import("./pages/MinhasAssinaturas"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Só notifica se a query tiver dados já em cache (refetch silencioso falhou)
      // ou se foi explicitamente marcada com meta.showErrorToast
      const showToast = query.state.data !== undefined || (query.meta as { showErrorToast?: boolean } | undefined)?.showErrorToast;
      log.error(
        { err: { message: (error as Error)?.message }, queryKey: query.queryKey },
        "react-query.query.error"
      );
      if (showToast) {
        toast.error("Não foi possível atualizar os dados.", {
          description: "Tente novamente em instantes.",
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      log.error(
        { err: { message: (error as Error)?.message }, mutationKey: mutation.options.mutationKey },
        "react-query.mutation.error"
      );
      // Mutations sem onError próprio recebem toast genérico
      if (!mutation.options.onError) {
        toast.error("Não foi possível salvar.", {
          description: "Tente novamente em instantes.",
        });
      }
    },
  }),
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light">
      <AuthProvider>
        <ThemeScheduleProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <OfflineIndicator />
          <BrowserRouter>
            <IdleLogoutMount />
            <RouteErrorBoundary>
            <Suspense fallback={<div className="flex min-h-[50vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>}>
            <Routes>
              <Route path="/" element={<Navigate to="/auth" replace />} />
              <Route path="/home" element={<Navigate to="/auth" replace />} />
              <Route path="/landing" element={<Navigate to="/auth" replace />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/planos" element={<PlanosB2C />} />
              <Route path="/checkout/:planCode" element={
                <ProtectedRoute><CheckoutB2C /></ProtectedRoute>
              } />
              <Route path="/checkout/retorno" element={
                <ProtectedRoute><CheckoutReturn /></ProtectedRoute>
              } />
              <Route path="/conta/assinaturas" element={
                <ProtectedRoute><MinhasAssinaturas /></ProtectedRoute>
              } />

              <Route path="/setup-mfa" element={<SetupMfa />} />
              <Route path="/account/security" element={<ProtectedRoute><AccountSecurity /></ProtectedRoute>} />
              <Route path="/plans" element={
                <ProtectedRoute><Plans /></ProtectedRoute>
              } />
              {/* /select-module descontinuado — redireciona para o módulo padrão (Carreiras Policiais). */}
              <Route path="/select-module" element={<Navigate to="/carreiras-policiais" replace />} />
              <Route path="/select-module/legacy" element={
                <ProtectedRoute><ModuleSelection /></ProtectedRoute>
              } />
              <Route path="/modulos/comprar/:module" element={
                <ProtectedRoute><ModulePurchase /></ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><Dashboard /></ModuleProtectedRoute></ProtectedRoute>
              } />
              {/* Concursos routes */}
              <Route path="/concursos/study-plan" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><ConcursosStudyPlan /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/concursos/autoavaliacao" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><ConcursosSelfAssessment /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/autoavaliacao" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemSelfAssessment /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/estatisticas" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemStats /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/onde-eu-estou" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemOndeEuEstou /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/mapa-ufs" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemMapaUFs /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/buscar-escola" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemBuscarEscola /></ModuleProtectedRoute></ProtectedRoute>
              } />

              <Route path="/questions" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><Questions /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/questions/:id" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><QuestionDetail /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/history" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><History /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute><Profile /></ProtectedRoute>
              } />
              <Route path="/simulados" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><Simulations /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/simulados/criar" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><CreateSimulation /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/simulados/:id" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><SimulationPlay /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/simulados/compartilhado/:shareCode" element={
                <SharedSimulation />
              } />
              <Route path="/ranking" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><Ranking /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/flashcards" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><Flashcards /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/flashcards/review/:disciplineId" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><FlashcardReview /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/pomodoro" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><PomodoroStudy /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/pomodoro/history" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><PomodoroHistory /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/pomodoro" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><PomodoroStudy /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/pomodoro/history" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><PomodoroHistory /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/oab/pomodoro" element={
                <ProtectedRoute><ModuleProtectedRoute module="oab"><PomodoroStudy /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/oab/pomodoro/history" element={
                <ProtectedRoute><ModuleProtectedRoute module="oab"><PomodoroHistory /></ModuleProtectedRoute></ProtectedRoute>
              } />
              {/* Vestibulares pomodoro: rotas geradas em ModuleRoutes.tsx */}

              <Route path="/analytics" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><Analytics /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/challenges" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><Challenges /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/groups" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><StudyGroups /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/groups/:id" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><StudyGroupDetail /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/smart-review" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><SmartReview /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/discipline-goals" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><DisciplineGoals /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/achievements" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><Achievements /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/favorites" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><Favorites /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/favorites/study" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><FavoritesStudy /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/study-stats" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><StudyStats /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/settings/concursos" element={
                <ProtectedRoute><ModuleProtectedRoute module="concursos"><ConcursosSettings /></ModuleProtectedRoute></ProtectedRoute>
              } />
              {/* Videoaulas routes */}
              <Route path="/videoaulas/meus-cursos" element={
                <ProtectedRoute><MyCourses /></ProtectedRoute>
              } />
              <Route path="/videoaulas" element={
                <ProtectedRoute><VideoaulasHome /></ProtectedRoute>
              } />
              <Route path="/videoaulas/catalogo" element={
                <ProtectedRoute><VideoLessons /></ProtectedRoute>
              } />
              <Route path="/videoaulas/historico" element={
                <ProtectedRoute><VideoaulasHistory /></ProtectedRoute>
              } />
              <Route path="/videoaulas/estatisticas" element={
                <ProtectedRoute><VideoaulasStats /></ProtectedRoute>
              } />
              <Route path="/videoaulas/:courseId" element={
                <ProtectedRoute><CourseDetail /></ProtectedRoute>
              } />
              <Route path="/videoaulas/:courseId/aula/:lessonId" element={
                <ProtectedRoute><LessonPlayer /></ProtectedRoute>
              } />
              {/* ENEM Routes */}
              <Route path="/enem/questions/:id" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><QuestionDetail /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemDashboard /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/questions" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemQuestions /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/simulations" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemSimulations /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/simulados/criar" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemCreateSimulation /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/errors" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemErrorPatterns /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/result/:attemptId" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemSimulationResult /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/review/:attemptId" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemQuestionReview /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/weak-areas" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemWeakAreasStudy /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/smart-review" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemSmartReview /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/full-simulation" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemFullSimulation /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/settings" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemSettings /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/ranking" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemRanking /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/study-plan" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemStudyPlan /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/redacao" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemRedacao /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/redacao/historico" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemRedacaoHistorico /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/redacao/novo/:themeId" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemRedacaoEditor /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/redacao/:attemptId" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemRedacaoResultado /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/oab/study-plan" element={
                <ProtectedRoute><ModuleProtectedRoute module="oab"><OabStudyPlan /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/oab/autoavaliacao" element={
                <ProtectedRoute><ModuleProtectedRoute module="oab"><OabSelfAssessment /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/cnu/study-plan" element={
                <ProtectedRoute><ModuleProtectedRoute module="cnu"><CnuStudyPlan /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/carreiras-policiais/study-plan" element={
                <ProtectedRoute><ModuleProtectedRoute module="carreiras-policiais"><PoliciaisStudyPlan /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/carreiras-juridicas/study-plan" element={
                <ProtectedRoute><ModuleProtectedRoute module="carreiras-juridicas"><JuridicasStudyPlan /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/tribunais/study-plan" element={
                <ProtectedRoute><ModuleProtectedRoute module="tribunais"><TribunaisStudyPlan /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/bancario/study-plan" element={
                <ProtectedRoute><ModuleProtectedRoute module="bancario"><BancarioStudyPlan /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/militares/study-plan" element={
                <ProtectedRoute><ModuleProtectedRoute module="militares"><MilitaresStudyPlan /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/residencia/study-plan" element={
                <ProtectedRoute><ModuleProtectedRoute module="residencia"><ResidenciaStudyPlan /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/vestibulares/study-plan" element={
                <ProtectedRoute><ModuleProtectedRoute module="vestibulares"><VestibularesStudyPlan /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/colegios/study-plan" element={
                <ProtectedRoute><ModuleProtectedRoute module="colegios"><ColegiosStudyPlan /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/achievements" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemAchievements /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/enem/analytics" element={
                <ProtectedRoute><ModuleProtectedRoute module="enem"><EnemAnalytics /></ModuleProtectedRoute></ProtectedRoute>
              } />
              {/* OAB Routes */}
              <Route path="/oab/questions/:id" element={
                <ProtectedRoute><ModuleProtectedRoute module="oab"><QuestionDetail /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/oab" element={
                <ProtectedRoute><ModuleProtectedRoute module="oab"><OabDashboard /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/oab/questions" element={
                <ProtectedRoute><ModuleProtectedRoute module="oab"><OabQuestions /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/oab/simulations" element={
                <ProtectedRoute><ModuleProtectedRoute module="oab"><OabSimulations /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/oab/simulations/create" element={
                <ProtectedRoute><ModuleProtectedRoute module="oab"><OabCreateSimulation /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/oab/ranking" element={
                <ProtectedRoute><ModuleProtectedRoute module="oab"><OabRanking /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/oab/settings" element={
                <ProtectedRoute><ModuleProtectedRoute module="oab"><OabSettings /></ModuleProtectedRoute></ProtectedRoute>
              } />
              <Route path="/oab/analytics" element={
                <ProtectedRoute><ModuleProtectedRoute module="oab"><OabAnalytics /></ModuleProtectedRoute></ProtectedRoute>
              } />
              {/* Rotas geradas dos 9 módulos clone (Tribunais, Policiais,
                  Jurídicas, CNU, Bancário, Militares, Residência, Colégios,
                  Vestibulares) — ver src/routes/ModuleRoutes.tsx. */}
              {renderModuleRoutes()}






              <Route path="/admin" element={
                <AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>
              } />
              <Route path="/admin/questions" element={
                <AdminProtectedRoute><AdminQuestions /></AdminProtectedRoute>
              } />
              <Route path="/admin/disciplines" element={
                <AdminProtectedRoute><AdminDisciplines /></AdminProtectedRoute>
              } />
              <Route path="/admin/exam-boards" element={
                <AdminProtectedRoute><AdminExamBoards /></AdminProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <AdminProtectedRoute><AdminUsers /></AdminProtectedRoute>
              } />
              <Route path="/admin/turmas" element={
                <AdminProtectedRoute><OwnerOnlyRoute><AdminClasses /></OwnerOnlyRoute></AdminProtectedRoute>
              } />
              <Route path="/admin/flashcards" element={
                <AdminProtectedRoute><AdminFlashcards /></AdminProtectedRoute>
              } />
              <Route path="/admin/simulations" element={
                <AdminProtectedRoute><AdminSimulations /></AdminProtectedRoute>
              } />
              <Route path="/admin/simulations/create" element={
                <AdminProtectedRoute><AdminCreateSimulation /></AdminProtectedRoute>
              } />
              <Route path="/admin/simulations/:id" element={
                <AdminProtectedRoute><AdminSimulationDetail /></AdminProtectedRoute>
              } />
              <Route path="/admin/subject-linking" element={
                <AdminProtectedRoute><AdminSubjectLinking /></AdminProtectedRoute>
              } />
              <Route path="/admin/policiais-whitelist" element={
                <AdminProtectedRoute><AdminPoliciaisWhitelist /></AdminProtectedRoute>
              } />
              <Route path="/admin/ai-auto-created" element={
                <AdminProtectedRoute><AdminAiAutoCreated /></AdminProtectedRoute>
              } />
              <Route path="/admin/subject-suggestions" element={
                <AdminProtectedRoute><AdminSubjectSuggestions /></AdminProtectedRoute>
              } />
              <Route path="/admin/discipline-consolidation" element={
                <AdminProtectedRoute><AdminDisciplineConsolidation /></AdminProtectedRoute>
              } />
              <Route path="/admin/flashcard-generator" element={
                <AdminProtectedRoute><AdminFlashcardGenerator /></AdminProtectedRoute>
              } />
              <Route path="/admin/enem-questions" element={
                <AdminProtectedRoute><AdminEnemQuestions /></AdminProtectedRoute>
              } />
              <Route path="/admin/enem-import" element={
                <AdminProtectedRoute><AdminEnemImport /></AdminProtectedRoute>
              } />
              <Route path="/admin/enem-data-import" element={
                <AdminProtectedRoute><AdminEnemDataImport /></AdminProtectedRoute>
              } />
              <Route path="/admin/enem-extraction/:paperId" element={
                <AdminProtectedRoute><AdminEnemExtractionReview /></AdminProtectedRoute>
              } />
              <Route path="/admin/fonte-import" element={
                <AdminProtectedRoute><AdminFonteImport /></AdminProtectedRoute>
              } />
              <Route path="/admin/tec-import" element={
                <AdminProtectedRoute><AdminTecImport /></AdminProtectedRoute>
              } />
              <Route path="/admin/questionei-reinfer" element={
                <AdminProtectedRoute><AdminQuestioneiReinfer /></AdminProtectedRoute>
              } />
              <Route path="/admin/qualidade-questoes" element={
                <AdminProtectedRoute><AdminQuestionQualityAudit /></AdminProtectedRoute>
              } />
              <Route path="/admin/discipline-stats" element={
                <AdminProtectedRoute><AdminDisciplineStats /></AdminProtectedRoute>
              } />
              <Route path="/admin/course-financials" element={
                <AdminProtectedRoute><OwnerOnlyRoute><AdminCourseFinancials /></OwnerOnlyRoute></AdminProtectedRoute>
              } />
              <Route path="/admin/billing" element={
                <AdminProtectedRoute><OwnerOnlyRoute><AdminBilling /></OwnerOnlyRoute></AdminProtectedRoute>
              } />
              <Route path="/admin/cron-jobs" element={<Navigate to="/admin/scrapers-monitor" replace />} />
              <Route path="/admin/export-import" element={
                <AdminProtectedRoute><AdminExportImport /></AdminProtectedRoute>
              } />
              <Route path="/admin/tenant-sync" element={
                <AdminProtectedRoute><OwnerOnlyRoute><AdminTenantSync /></OwnerOnlyRoute></AdminProtectedRoute>
              } />
              <Route path="/admin/analytics" element={
                <AdminProtectedRoute><AdminAnalytics /></AdminProtectedRoute>
              } />
              <Route path="/admin/oab-import" element={
                <AdminProtectedRoute><AdminOabImport /></AdminProtectedRoute>
              } />
              <Route path="/admin/oab-questions" element={
                <AdminProtectedRoute><AdminOabQuestions /></AdminProtectedRoute>
              } />
              <Route path="/admin/residencia-questions" element={
                <AdminProtectedRoute><AdminResidenciaQuestions /></AdminProtectedRoute>
              } />
              <Route path="/admin/revalida-questions" element={
                <AdminProtectedRoute><AdminRevalidaQuestions /></AdminProtectedRoute>
              } />
              <Route path="/admin/revalida-essays" element={
                <AdminProtectedRoute><AdminRevalidaEssays /></AdminProtectedRoute>
              } />
              <Route path="/admin/courses" element={
                <AdminProtectedRoute><AdminCourses /></AdminProtectedRoute>
              } />
              <Route path="/admin/teachers" element={
                <AdminProtectedRoute><AdminTeachers /></AdminProtectedRoute>
              } />
              <Route path="/admin/plan-requests" element={
                <AdminProtectedRoute><AdminPlanRequests /></AdminProtectedRoute>
              } />
              <Route path="/admin/ai-usage" element={
                <AdminProtectedRoute><OwnerOnlyRoute><AdminAIUsage /></OwnerOnlyRoute></AdminProtectedRoute>
              } />
              <Route path="/admin/scrapers-monitor" element={
                <AdminProtectedRoute><OwnerOnlyRoute><AdminScrapersMonitor /></OwnerOnlyRoute></AdminProtectedRoute>
              } />
              <Route path="/admin/residencia-scrapers" element={
                <AdminProtectedRoute><OwnerOnlyRoute><AdminResidenciaScrapers /></OwnerOnlyRoute></AdminProtectedRoute>
              } />
              <Route path="/admin/revalida-import" element={
                <AdminProtectedRoute><RevalidaImport /></AdminProtectedRoute>
              } />
              <Route path="/residencia/revalida-discursivas" element={
                <ProtectedRoute><RevalidaDiscursivas /></ProtectedRoute>
              } />
              <Route path="/admin/security" element={
                <AdminProtectedRoute><OwnerOnlyRoute><AdminSecurityMonitor /></OwnerOnlyRoute></AdminProtectedRoute>
              } />
              <Route path="/admin/system-ratings" element={
                <AdminProtectedRoute><AdminSystemRatings /></AdminProtectedRoute>
              } />
              <Route path="/admin/question-reports" element={
                <AdminProtectedRoute><AdminQuestionReports /></AdminProtectedRoute>
              } />
              <Route path="/admin/ai-pending" element={
                <AdminProtectedRoute><AdminAiPendingQueue /></AdminProtectedRoute>
              } />
              <Route path="/admin/plan-pricing" element={
                <AdminProtectedRoute><OwnerOnlyRoute><AdminPlanPricing /></OwnerOnlyRoute></AdminProtectedRoute>
              } />
              <Route path="/admin/dev/plan-pricing-b2c" element={
                <AdminProtectedRoute><OwnerOnlyRoute><AdminDevPlanPricingB2C /></OwnerOnlyRoute></AdminProtectedRoute>
              } />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            </RouteErrorBoundary>
          </BrowserRouter>
          </TooltipProvider>
        </ThemeScheduleProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
